import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft, Ban, Bell, BellOff, CalendarDays,
  Edit3, KeyRound, TrendingDown, TrendingUp, Wallet,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../services/api";
import { getMediaUrl } from "../services/api";
import AdminLayout from "./AdminLayout";

const TABS = ["bets", "dw", "bonus", "winnings", "casino"];
const TAB_LABELS = { bets: "Bets", dw: "Deposits & Withdrawals", bonus: "Bonus", winnings: "Winnings", casino: "Casino" };

function AdminUserProfile() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState("bets");
  const [editOpen, setEditOpen] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const [walletMode, setWalletMode] = useState("deposit");
  const [walletAmt,  setWalletAmt]  = useState("");
  const [walletNote, setWalletNote] = useState("");
  const [expandedBet, setExpandedBet] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [datePreset, setDatePreset] = useState("all");
  const [dateFrom,   setDateFrom]   = useState(new Date().toISOString().slice(0,10));
  const [dateTo,     setDateTo]     = useState(new Date().toISOString().slice(0,10));
  const [editForm,   setEditForm]   = useState({
    name:"", phone:"", withdraw_limit_per_day:"", ank_limit:""
  });

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const p = new URLSearchParams();
      if (datePreset !== "all" && dateFrom && dateTo) {
        p.set("date_from", dateFrom);
        p.set("date_to",   dateTo);
      }
      const res = await API.get(`/admin/users/${id}/profile${p.toString() ? `?${p}` : ""}`);
      const d = res.data || null;
      setData(d);
      if (d?.user) {
        setEditForm({
          name:  d.user.name  || "",
          phone: d.user.phone || "",
          withdraw_limit_per_day: d.user.withdraw_limit_per_day != null ? String(d.user.withdraw_limit_per_day) : "",
          ank_limit: d.user.ank_limit != null ? String(d.user.ank_limit) : "",
        });
      }
    } catch (e) {
      console.log(e); setData(null);
    } finally {
      setLoading(false);
    }
  }, [id, datePreset, dateFrom, dateTo]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  useEffect(() => {
    if (datePreset === "today") {
      const t = new Date().toISOString().slice(0,10);
      setDateFrom(t); setDateTo(t);
    }
  }, [datePreset]);

  const saveEdit = async () => {
    try {
      setSavingEdit(true);
      await API.put(`/admin/users/${id}`, {
        ...editForm,
        ank_limit: editForm.ank_limit === "" ? null : Number(editForm.ank_limit),
        withdraw_limit_per_day: editForm.withdraw_limit_per_day === "" ? null : Number(editForm.withdraw_limit_per_day),
      });
      setEditOpen(false);
      await loadProfile();
    } finally { setSavingEdit(false); }
  };

  const toggleBlock = async () => {
    if (!data?.user) return;
    await API.put(`/admin/users/${id}`, {
      status: (data.user.status || "Active") === "Blocked" ? "Active" : "Blocked"
    });
    loadProfile();
  };

  const toggleAlerts = async () => {
    if (!data?.user) return;
    const next = Number(data.user.result_notifications_enabled ?? 1) ? 0 : 1;
    await API.put(`/admin/users/${id}`, { result_notifications_enabled: next });
    loadProfile();
  };

  const genLoginCode = async () => {
    try {
      const r = await API.post(`/admin/users/${id}/login-code`);
      const code = String(r.data?.code || "");
      if (code && navigator.clipboard?.writeText) navigator.clipboard.writeText(code).catch(()=>{});
      alert(`Login code: ${code}\nCopied to clipboard.`);
      loadProfile();
    } catch (e) { alert(e?.response?.data?.message || "Error"); }
  };

  const submitWallet = async () => {
    try {
      await API.post("/admin/users/manual-transaction", {
        user_id: id, mode: walletMode, amount: walletAmt, remark: walletNote,
      });
      setWalletOpen(false); setWalletAmt(""); setWalletNote("");
      loadProfile();
    } catch (e) { alert(e?.response?.data?.message || "Error"); }
  };

  const toggleDwEntry = async (type, entryId, exclude) => {
    await API.post(`/admin/users/${id}/dw-entries/${type}/${entryId}/exclude`, { exclude: exclude?1:0 });
    loadProfile();
  };

  /* derived */
  const user    = data?.user    || {};
  const stats   = data?.stats   || {};
  const lists   = data?.lists   || {};
  const settings = data?.settings || {};
  const betList  = useMemo(() => lists.bets || [], [lists.bets]);
  const groupedBets = useMemo(() => groupBets(betList), [betList]);
  const dwList   = lists.dw       || [];
  const bonusList = lists.bonus   || [];
  const winList  = lists.winnings || [];
  const casinoList = lists.casino || [];
  const isBlocked  = (user.status || "Active") === "Blocked";
  const alertsOn   = Number(user.result_notifications_enabled ?? 1) !== 0;
  const totalBal   = +user.balance  || 0;
  const winBal     = +user.winnings || 0;
  const depBal     = Math.max(totalBal - winBal, 0);
  const netDW      = (+stats.total_deposit||0) - (+stats.total_withdraw||0);
  const winRate    = stats.total_bets ? Math.round((+stats.won_bets/+stats.total_bets)*100) : 0;

  return (
    <AdminLayout>
      <div style={page}>

        {/* ── hero banner ── */}
        <div style={hero}>

          {/* back */}
          <button style={backBtn} onClick={() => navigate("/admin/users")}>
            <ArrowLeft size={18} color="#fff"/>
          </button>

          {/* avatar + name */}
          <div style={heroLeft}>
            <div style={heroBigAvatar}>
              {user.profile_image
                ? <img src={getMediaUrl(user.profile_image)} alt="" style={heroAvatarImg}/>
                : <span style={heroAvatarLetter}>{(user.name||"U").charAt(0).toUpperCase()}</span>}
              <span style={heroDot(getSessionLabel(user))}/>
            </div>
            <div>
              <div style={heroName}>{user.name || "User"}</div>
              <div style={heroPhone}>{user.phone || "—"}</div>
              <div style={heroPills}>
                <span style={pill(isBlocked?"#fee2e2":"#dcfce7", isBlocked?"#dc2626":"#16a34a")}>
                  {isBlocked?"Blocked":"Active"}
                </span>
                <span style={pill("#ffedd5","#ea580c")}>ID {user.id || id}</span>
                {user.agent && <span style={pill("#fef3c7","#b45309")}>{user.agent}</span>}
                {user.referral_code && <span style={pill("#f0fdf4","#15803d")}>{user.referral_code}</span>}
                {user.fallback_login_code && <span style={pill("#fffbeb","#92400e")}>Code: {user.fallback_login_code}</span>}
              </div>
              <div style={heroJoined}>Joined {fmtDate(user.created_at)}</div>
            </div>
          </div>

          {/* hero action buttons */}
          <div style={heroActions}>
            <HeroBtn onClick={() => setEditOpen(true)} icon={<Edit3 size={15}/>} label="Edit"/>
            <HeroBtn onClick={genLoginCode} icon={<KeyRound size={15}/>} label="Code"/>
            <HeroBtn onClick={toggleAlerts}
              icon={alertsOn ? <Bell size={15}/> : <BellOff size={15}/>}
              label={alertsOn?"Alerts ON":"Alerts OFF"}
              accent={alertsOn?"#22c55e":"#94a3b8"}/>
            <HeroBtn onClick={toggleBlock}
              icon={<Ban size={15}/>}
              label={isBlocked?"Unblock":"Block"}
              accent={isBlocked?"#22c55e":"#ef4444"}/>
            <HeroBtn onClick={() => setWalletOpen(true)} icon={<Wallet size={15}/>} label="Wallet" accent="#f97316"/>
          </div>
        </div>

        {loading && <div style={loadBox}>Loading profile…</div>}
        {!loading && !data && <div style={loadBox}>User not found.</div>}

        {!loading && data && (<>

          {/* ── balance strip ── */}
          <div style={balStrip}>
            <BalCell label="Deposit Bal" value={fmtMoney(depBal)} color="#ea580c"/>
            <div style={balDiv}/>
            <BalCell label="Winning Bal" value={fmtMoney(winBal)} color="#16a34a"/>
            <div style={balDiv}/>
            <BalCell label="Total Balance" value={fmtMoney(totalBal)} color="#f97316" big/>
          </div>

          {/* ── 8 stat cards ── */}
          <div style={statsGrid}>
            <Stat label="Total Deposit"   value={fmtMoney(stats.total_deposit)}   col="#16a34a"/>
            <Stat label="Total Withdraw"  value={fmtMoney(stats.total_withdraw)}   col="#dc2626"/>
            <Stat label="Net D / W"       value={fmtMoney(netDW)}                  col={netDW>=0?"#16a34a":"#dc2626"}/>
            <Stat label="Total Bets"      value={stats.total_bets||0}              col="#ea580c"/>
            <Stat label="Bet Amount"      value={fmtMoney(stats.total_bet_amount)} col="#0891b2"/>
            <Stat label="Win Amount"      value={fmtMoney(stats.total_win_amount)} col="#16a34a"/>
            <Stat label="Win Rate"        value={`${winRate}%`}                    col="#f97316"/>
            <Stat label="Won / Lost"      value={`${stats.won_bets||0} / ${stats.lost_bets||0}`} col="#334155"/>
          </div>

          {/* ── limits card ── */}
          <div style={limitsCard}>
            <div style={limitsTitle}>User Limits</div>
            <div style={limitsGrid}>
              <LimitRow label="Ank Limit" value={user.ank_limit != null ? user.ank_limit : settings.default_ank_limit != null ? `Global (${settings.default_ank_limit})` : "Default"}/>
              <LimitRow label="Withdraw / Day" value={user.withdraw_limit_per_day===0?"No limit":user.withdraw_limit_per_day||"Default"}/>
              <LimitRow label="Last Login" value={fmtDateTime(user.last_login)}/>
              <LimitRow label="Last Activity" value={fmtDateTime(stats.last_activity_at||stats.last_bet_at)}/>
            </div>
          </div>

          {/* ── date filter ── */}
          <div style={dateBar}>
            <div style={datePresets}>
              {["all","today","custom"].map((p)=>(
                <button key={p} style={dateChip(datePreset===p)} onClick={()=>setDatePreset(p)}>
                  {p==="all"?"All Time":p==="today"?"Today":"Custom"}
                </button>
              ))}
            </div>
            {datePreset!=="all" && (
              <div style={dateInputRow}>
                <CalendarDays size={14} color="#64748b"/>
                <input type="date" style={dateInp} value={dateFrom}
                  onChange={(e)=>{setDatePreset("custom");setDateFrom(e.target.value);}}/>
                <span style={{color:"#64748b",fontSize:13}}>→</span>
                <input type="date" style={dateInp} value={dateTo}
                  onChange={(e)=>{setDatePreset("custom");setDateTo(e.target.value);}}/>
              </div>
            )}
          </div>

          {/* ── tab bar ── */}
          <div style={tabBar}>
            {TABS.map((t)=>(
              <button key={t} style={tabBtn(tab===t)} onClick={()=>setTab(t)}>
                {TAB_LABELS[t]}
                <span style={tabCount(tab===t)}>
                  {t==="bets"?groupedBets.length:t==="dw"?dwList.length:t==="bonus"?bonusList.length:t==="casino"?casinoList.length:winList.length}
                </span>
              </button>
            ))}
          </div>

          {/* ── content tables ── */}
          {tab==="bets" && (
            <div style={tableCard}>
              <table style={tbl}>
                <thead><tr>
                  <th style={th}></th>
                  <th style={th}>Market</th>
                  <th style={th}>Type</th>
                  <th style={th}>Numbers</th>
                  <th style={th}>Amount</th>
                  <th style={th}>Status</th>
                  <th style={th}>Date</th>
                </tr></thead>
                <tbody>
                  {groupedBets.map((g) => {
                    const open = expandedBet === g.key;
                    return (
                      <React.Fragment key={g.key}>
                        <tr style={tRow}>
                          <td style={td}>
                            <button style={viewBtn} onClick={()=>setExpandedBet(open?"":g.key)}>
                              {open?"▲":"▼"}
                            </button>
                          </td>
                          <td style={td}>
                            <div style={{fontWeight:800,color:"#431407"}}>{g.market_name}</div>
                            <div style={sub}>{g.session} · {g.dateLabel}</div>
                          </td>
                          <td style={td}><span style={typePill}>{g.baseType}</span></td>
                          <td style={td}>
                            <div style={numWrap}>
                              {g.previewNums.map((n)=><span key={n} style={numChip}>{n}</span>)}
                              {g.extra>0 && <span style={moreChip}>+{g.extra}</span>}
                            </div>
                          </td>
                          <td style={td}>
                            <div style={{fontWeight:900,color:"#431407"}}>{fmtMoney(g.amount)}</div>
                            <div style={sub}>Payout {fmtMoney(g.payout)}</div>
                          </td>
                          <td style={td}><StatusPill s={g.status}/></td>
                          <td style={td}><div style={{fontSize:12}}>{fmtDateTime(g.latestAt)}</div></td>
                        </tr>
                        {open && (
                          <tr><td colSpan={7} style={{padding:"0 16px 12px"}}>
                            <div style={expandPanel}>
                              {g.rows.map((r)=>(
                                <div key={r.id} style={expandRow}>
                                  <span style={numChip}>{r.number}</span>
                                  <span style={{fontSize:12,color:"#64748b"}}>{prettyType(r.type)}</span>
                                  <span style={{fontWeight:800,fontSize:13}}>{fmtMoney(r.amount)}</span>
                                  <StatusPill s={r.status||"pending"}/>
                                  <span style={{fontSize:11,color:"#94a3b8"}}>{fmtDateTime(r.created_at||r.date)}</span>
                                </div>
                              ))}
                            </div>
                          </td></tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {!groupedBets.length && <Empty cols={7} label="No bets"/>}
                </tbody>
              </table>
            </div>
          )}

          {tab==="dw" && (
            <div style={tableCard}>
              <table style={tbl}>
                <thead><tr>
                  <th style={th}>ID</th>
                  <th style={th}>Type</th>
                  <th style={th}>Amount</th>
                  <th style={th}>Status</th>
                  <th style={th}>Date</th>
                  <th style={th}>Remark</th>
                  <th style={th}>Action</th>
                </tr></thead>
                <tbody>
                  {dwList.map((item)=>{
                    const isW = String(item.type||"").toLowerCase()==="withdraw";
                    const excluded = Number(item.exclude_from_reports||0)>0;
                    return (
                      <tr key={`${item.type}-${item.id}`} style={{...tRow, opacity:excluded?.6:1}}>
                        <td style={td}><span style={idChip}>#{item.id}</span></td>
                        <td style={td}>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <span style={{...dwIcon,background:isW?"#fee2e2":"#dcfce7",color:isW?"#dc2626":"#16a34a"}}>
                              {isW?<TrendingUp size={12}/>:<TrendingDown size={12}/>}
                            </span>
                            <span style={{fontWeight:800,color:isW?"#dc2626":"#16a34a"}}>{item.type}</span>
                          </div>
                        </td>
                        <td style={td}><span style={{fontWeight:900,color:isW?"#dc2626":"#16a34a",fontSize:14}}>{fmtMoney(item.amount)}</span></td>
                        <td style={td}><StatusPill s={item.status}/></td>
                        <td style={td}><span style={{fontSize:12,color:"#64748b"}}>{fmtDateTime(item.created_at)}</span></td>
                        <td style={td}>
                          <span style={{fontSize:12,color:"#334155"}}>{item.remark||"—"}</span>
                          {excluded && <div style={{fontSize:10,color:"#dc2626",fontWeight:700,marginTop:2}}>Removed from reports</div>}
                        </td>
                        <td style={td}>
                          <button
                            style={excluded?restoreBtn:removeBtn}
                            onClick={()=>toggleDwEntry(isW?"withdraw":"deposit", item.id, excluded?0:1)}>
                            {excluded?"Restore":"Remove"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {!dwList.length && <Empty cols={7} label="No D/W records"/>}
                </tbody>
              </table>
            </div>
          )}

          {tab==="bonus" && (
            <div style={tableCard}>
              <table style={tbl}>
                <thead><tr>
                  <th style={th}>ID</th>
                  <th style={th}>Type</th>
                  <th style={th}>Amount</th>
                  <th style={th}>Status</th>
                  <th style={th}>Date</th>
                  <th style={th}>Remark</th>
                </tr></thead>
                <tbody>
                  {bonusList.map((b)=>(
                    <tr key={b.id} style={tRow}>
                      <td style={td}><span style={idChip}>#{b.id}</span></td>
                      <td style={td}><span style={{fontWeight:800,color:"#ea580c"}}>{b.type}</span></td>
                      <td style={td}><span style={{fontWeight:900,color:"#ea580c",fontSize:14}}>{fmtMoney(b.amount)}</span></td>
                      <td style={td}><StatusPill s={b.status}/></td>
                      <td style={td}><span style={{fontSize:12,color:"#64748b"}}>{fmtDateTime(b.created_at)}</span></td>
                      <td style={td}><span style={{fontSize:12,color:"#334155"}}>{b.remark||"—"}</span></td>
                    </tr>
                  ))}
                  {!bonusList.length && <Empty cols={6} label="No bonus records"/>}
                </tbody>
              </table>
            </div>
          )}

          {tab==="casino" && (() => {
            const cWagered = stats.casino_wagered || 0;
            const cWon     = stats.casino_won     || 0;
            const cNet     = cWon - cWagered;
            const cWinRate = stats.casino_total_rounds
              ? Math.round((stats.casino_win_rounds / stats.casino_total_rounds) * 100) : 0;
            const gameNames = { ver_andbr:"Andar Bahar", ver_gondb:"Andar Bahar (Pro)", ver_tnpfo:"Teen Patti", ver_dt7m:"Dragon Tiger", ver_vdt:"VDT", Aviator:"Aviator" };
            return (
              <div>
                {/* Casino summary cards */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:10,marginBottom:14}}>
                  {[
                    {label:"Total Rounds",    val: stats.casino_total_rounds||0,      col:"#ea580c"},
                    {label:"Total Bets",      val: stats.casino_total_bets||0,         col:"#0891b2"},
                    {label:"Total Wagered",   val: `₹${Number(cWagered).toLocaleString("en-IN")}`, col:"#334155"},
                    {label:"Total Won",       val: `₹${Number(cWon).toLocaleString("en-IN")}`,     col:"#16a34a"},
                    {label:"Net P&L (user)",  val: `${cNet>=0?"+":""}₹${Number(cNet).toLocaleString("en-IN")}`, col:cNet>=0?"#16a34a":"#dc2626"},
                    {label:"Win Rounds",      val: stats.casino_win_rounds||0,         col:"#16a34a"},
                    {label:"Loss Rounds",     val: stats.casino_loss_rounds||0,        col:"#dc2626"},
                    {label:"Win Rate",        val: `${cWinRate}%`,                     col:"#f97316"},
                  ].map(c=>(
                    <div key={c.label} style={{background:"#fff",borderRadius:10,padding:"12px 14px",boxShadow:"0 1px 4px #0001",border:"1px solid #f1f5f9"}}>
                      <div style={{fontSize:11,color:"#64748b",fontWeight:600,marginBottom:4}}>{c.label}</div>
                      <div style={{fontSize:18,fontWeight:900,color:c.col}}>{c.val}</div>
                    </div>
                  ))}
                </div>
                {/* Casino bet history */}
                <div style={tableCard}>
                  <table style={tbl}>
                    <thead><tr>
                      <th style={th}>Round</th>
                      <th style={th}>Game</th>
                      <th style={th}>Side</th>
                      <th style={th}>Bet</th>
                      <th style={th}>Won</th>
                      <th style={th}>P&L</th>
                      <th style={th}>Status</th>
                      <th style={th}>Date</th>
                    </tr></thead>
                    <tbody>
                      {casinoList.map((c)=>{
                        const pnl = Number(c.win_amount||0) - Number(c.bet_amount||0);
                        const gName = gameNames[c.game_id] || c.game_id || `tpGame ${c.tp_game_id}`;
                        return (
                          <tr key={c.id} style={tRow}>
                            <td style={td}><span style={{fontSize:11,color:"#64748b",fontFamily:"monospace"}}>{String(c.round_id||"").slice(-8)}</span></td>
                            <td style={td}><span style={{fontSize:12,fontWeight:700,color:"#ea580c"}}>{gName}</span></td>
                            <td style={td}><span style={{fontSize:12,color:"#334155"}}>{c.runner_name||"—"}</span></td>
                            <td style={td}><span style={{fontWeight:800,color:"#431407"}}>{fmtMoney(c.bet_amount)}</span></td>
                            <td style={td}><span style={{fontWeight:800,color:"#16a34a"}}>{Number(c.win_amount||0)>0?fmtMoney(c.win_amount):"—"}</span></td>
                            <td style={td}><span style={{fontWeight:900,color:pnl>=0?"#16a34a":"#dc2626"}}>{pnl>=0?"+":""}{fmtMoney(pnl)}</span></td>
                            <td style={td}><StatusPill s={c.status}/></td>
                            <td style={td}><span style={{fontSize:11,color:"#64748b"}}>{fmtDateTime(c.settled_at||c.created_at)}</span></td>
                          </tr>
                        );
                      })}
                      {!casinoList.length && <Empty cols={8} label="No casino bets"/>}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {tab==="winnings" && (
            <div style={tableCard}>
              <table style={tbl}>
                <thead><tr>
                  <th style={th}>ID</th>
                  <th style={th}>Market</th>
                  <th style={th}>Type</th>
                  <th style={th}>Number</th>
                  <th style={th}>Bet Amt</th>
                  <th style={th}>Win Amt</th>
                  <th style={th}>Status</th>
                  <th style={th}>Date</th>
                </tr></thead>
                <tbody>
                  {winList.map((w)=>(
                    <tr key={w.id} style={tRow}>
                      <td style={td}><span style={idChip}>#{w.id}</span></td>
                      <td style={td}><span style={{fontWeight:700,color:"#431407",fontSize:12}}>{w.market_name||"—"}</span></td>
                      <td style={td}><span style={typePill}>{prettyType(w.type||"")}</span></td>
                      <td style={td}><span style={numChip}>{w.number||"—"}</span></td>
                      <td style={td}><span style={{fontWeight:800,color:"#64748b"}}>{fmtMoney(w.bid_amount)}</span></td>
                      <td style={td}><span style={{fontWeight:900,color:"#16a34a",fontSize:14}}>{fmtMoney(w.win_amount)}</span></td>
                      <td style={td}><StatusPill s={w.status}/></td>
                      <td style={td}><span style={{fontSize:12,color:"#64748b"}}>{fmtDateTime(w.created_at)}</span></td>
                    </tr>
                  ))}
                  {!winList.length && <Empty cols={8} label="No winning records"/>}
                </tbody>
              </table>
            </div>
          )}

        </>)}

        {/* ── Edit modal ── */}
        {editOpen && (
          <Overlay onClose={()=>setEditOpen(false)}>
            <div style={mHead}><span style={mTitle}>Edit User</span><CloseX onClick={()=>setEditOpen(false)}/></div>
            <div style={mBody}>
              <FieldRow label="Name">
                <input style={mInp} value={editForm.name} onChange={(e)=>setEditForm(f=>({...f,name:e.target.value}))}/>
              </FieldRow>
              <FieldRow label="Phone">
                <input style={mInp} value={editForm.phone} onChange={(e)=>setEditForm(f=>({...f,phone:e.target.value}))}/>
              </FieldRow>
              <FieldRow label="Ank Limit">
                <input style={mInp} placeholder="Leave blank for global" value={editForm.ank_limit}
                  onChange={(e)=>setEditForm(f=>({...f,ank_limit:e.target.value.replace(/\D/g,"").slice(0,4)}))}/>
              </FieldRow>
              <FieldRow label="Withdraw / Day">
                <select style={mInp} value={editForm.withdraw_limit_per_day}
                  onChange={(e)=>setEditForm(f=>({...f,withdraw_limit_per_day:e.target.value}))}>
                  <option value="">Default</option>
                  <option value="1">1 per day</option>
                  <option value="2">2 per day</option>
                  <option value="0">No limit</option>
                </select>
              </FieldRow>
            </div>
            <div style={mFoot}>
              <button style={mCancel} onClick={()=>setEditOpen(false)}>Cancel</button>
              <button style={mSave}   onClick={saveEdit} disabled={savingEdit}>
                {savingEdit?"Saving…":"Save"}
              </button>
            </div>
          </Overlay>
        )}

        {/* ── Wallet modal ── */}
        {walletOpen && (
          <Overlay onClose={()=>setWalletOpen(false)}>
            <div style={mHead}><span style={mTitle}>Manage Wallet</span><CloseX onClick={()=>setWalletOpen(false)}/></div>
            <div style={mBody}>
              <div style={walletModeRow}>
                {[["deposit","Deposit","#10b981"],["withdraw","Withdraw","#ef4444"]].map(([m,l,c])=>(
                  <button key={m} style={walletModeBtn(walletMode===m,c)} onClick={()=>setWalletMode(m)}>
                    {m==="deposit"?<TrendingDown size={14}/>:<TrendingUp size={14}/>} {l}
                  </button>
                ))}
              </div>
              <FieldRow label="Amount (Rs)">
                <input style={mInp} placeholder="Enter amount" value={walletAmt}
                  onChange={(e)=>setWalletAmt(e.target.value)}/>
              </FieldRow>
              <div style={quickRow}>
                {["200","500","1000","5000","10000"].map((v)=>(
                  <button key={v} style={qBtn} onClick={()=>setWalletAmt(v)}>
                    {v==="1000"?"1K":v==="5000"?"5K":v==="10000"?"10K":v}
                  </button>
                ))}
              </div>
              <FieldRow label="Remark">
                <input style={mInp} placeholder="Optional note" value={walletNote}
                  onChange={(e)=>setWalletNote(e.target.value)}/>
              </FieldRow>
            </div>
            <div style={mFoot}>
              <button style={mCancel} onClick={()=>setWalletOpen(false)}>Cancel</button>
              <button style={{...mSave,background:walletMode==="deposit"?"#10b981":"#ef4444"}} onClick={submitWallet}>
                {walletMode==="deposit"?"Deposit":"Withdraw"}
              </button>
            </div>
          </Overlay>
        )}

      </div>
    </AdminLayout>
  );
}

/* ─── sub-components ─── */

function HeroBtn({ onClick, icon, label, accent="#fff" }) {
  return (
    <button style={heroBtnStyle(accent)} onClick={onClick}>
      {icon}<span>{label}</span>
    </button>
  );
}

function BalCell({ label, value, color, big }) {
  return (
    <div style={balCell}>
      <div style={{fontSize:11,color:"rgba(255,255,255,0.6)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{label}</div>
      <div style={{fontSize:big?24:18,fontWeight:900,color:"#fff"}}>{value}</div>
    </div>
  );
}

function Stat({ label, value, col }) {
  return (
    <div style={statCard}>
      <div style={{fontSize:11,color:"#94a3b8",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>{label}</div>
      <div style={{fontSize:20,fontWeight:900,color:col}}>{value||"—"}</div>
    </div>
  );
}

function LimitRow({ label, value }) {
  return (
    <div style={limitRow}>
      <span style={{fontSize:12,color:"#94a3b8",fontWeight:600}}>{label}</span>
      <span style={{fontSize:13,fontWeight:800,color:"#431407"}}>{value||"—"}</span>
    </div>
  );
}

function StatusPill({ s }) {
  const v = String(s||"").toLowerCase();
  const map = { won:["#dcfce7","#16a34a"], approved:["#dcfce7","#16a34a"], success:["#dcfce7","#16a34a"],
    lost:["#fee2e2","#dc2626"], rejected:["#fee2e2","#dc2626"], blocked:["#fee2e2","#dc2626"],
    pending:["#fef3c7","#b45309"] };
  const [bg,col] = map[v] || ["#f1f5f9","#64748b"];
  return <span style={{...pillBase,background:bg,color:col}}>{s||"—"}</span>;
}

function Overlay({ onClose, children }) {
  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalBox} onClick={(e)=>e.stopPropagation()}>{children}</div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div style={{display:"grid",gap:6}}>
      <label style={{fontSize:12,fontWeight:700,color:"#475569"}}>{label}</label>
      {children}
    </div>
  );
}

function CloseX({ onClick }) {
  return <button style={{border:0,background:"transparent",fontSize:24,color:"#94a3b8",cursor:"pointer",lineHeight:1}} onClick={onClick}>×</button>;
}

function Empty({ cols, label }) {
  return <tr><td colSpan={cols} style={{padding:36,textAlign:"center",color:"#94a3b8",fontSize:13}}>{label}</td></tr>;
}

/* ─── helpers ─── */

function getSessionLabel(u={}) {
  const app = Number(u.has_app_session||0)>0;
  const web = Number(u.has_web_session||0)>0;
  if (app&&web) return "App+Web";
  if (app) return "App";
  if (web) return "Web";
  return "Offline";
}

function fmtMoney(v) {
  const n = +v||0;
  if (Math.abs(n)>=100000) return `₹${(n/100000).toFixed(1)}L`;
  if (Math.abs(n)>=1000) return `₹${(n/1000).toFixed(1)}K`;
  return `₹${n.toFixed(2)}`;
}

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"});
}

function fmtDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d) ? "—" : d.toLocaleString("en-IN",{day:"2-digit",month:"short",year:"2-digit",hour:"2-digit",minute:"2-digit"});
}

function prettyType(v="") {
  return String(v).replace(/[-_]/g," ").replace(/\b\w/g,(c)=>c.toUpperCase());
}

function groupBets(bets=[]) {
  const map = new Map();
  bets.forEach((b)=>{
    const raw = String(b.type||"").toLowerCase();
    const session = raw.includes("close")?"CLOSE":"OPEN";
    const baseType = raw.replace(/[-_](open|close)$/i,"").replace(/[-_]/g," ").toUpperCase();
    const key = [b.market_id||b.market_name, String(b.bet_date||b.created_at||"").slice(0,10), b.time_slot||session].join("|");
    if (!map.has(key)) map.set(key,{
      key, market_name:b.market_name||"-", baseType, session:b.time_slot||session,
      dateLabel:fmtDate(b.bet_date||b.created_at), latestAt:b.created_at||b.date,
      amount:0, payout:0, rows:[], statusCounts:{},
    });
    const g = map.get(key);
    g.rows.push(b);
    g.amount += +b.amount||0;
    g.payout += +b.payout||0;
    const st = String(b.status||"pending").toLowerCase();
    g.statusCounts[st] = (g.statusCounts[st]||0)+1;
    if (!g.latestAt || new Date(b.created_at||0)>new Date(g.latestAt)) g.latestAt = b.created_at||b.date;
  });
  return Array.from(map.values()).map((g)=>{
    const status = g.statusCounts.won?"won":g.statusCounts.pending?"pending":g.statusCounts.lost?"lost":"mixed";
    const nums = [...new Set(g.rows.map((r)=>String(r.number||"")).filter(Boolean))];
    return { ...g, status, previewNums:nums.slice(0,5), extra:Math.max(nums.length-5,0),
      rows:g.rows.sort((a,b)=>+b.id - +a.id) };
  }).sort((a,b)=>new Date(b.latestAt||0)-new Date(a.latestAt||0));
}

/* ─── dynamic styles ─── */

function pill(bg, col) {
  return { display:"inline-flex", alignItems:"center", padding:"4px 10px", borderRadius:999, background:bg, color:col, fontWeight:800, fontSize:11 };
}

function heroDot(label) {
  const col = label==="Offline"?"#94a3b8":label==="App+Web"?"#a855f7":"#22c55e";
  return { position:"absolute", right:2, bottom:2, width:14, height:14, borderRadius:"50%", background:col, border:"3px solid rgba(255,255,255,0.3)" };
}

function heroBtnStyle(accent) {
  return {
    display:"flex", alignItems:"center", gap:6,
    height:36, padding:"0 14px", borderRadius:10,
    border:`1.5px solid ${accent==="white"||accent==="#fff"?"rgba(255,255,255,0.25)":"rgba(255,255,255,0.2)"}`,
    background:"rgba(255,255,255,0.12)",
    color:"#fff", fontWeight:800, fontSize:12, cursor:"pointer",
    backdropFilter:"blur(4px)",
  };
}

function dateChip(active) {
  return {
    height:34, padding:"0 14px", borderRadius:10, border:"none",
    background: active ? "#ea580c" : "#f1f5f9",
    color: active ? "#fff" : "#64748b",
    fontWeight:700, fontSize:12, cursor:"pointer",
  };
}

function tabBtn(active) {
  return {
    height:42, padding:"0 18px", borderRadius:0, border:"none",
    borderBottom: active ? "2.5px solid #ea580c" : "2.5px solid transparent",
    background:"transparent",
    color: active ? "#ea580c" : "#64748b",
    fontWeight:800, fontSize:13, cursor:"pointer",
    display:"flex", alignItems:"center", gap:8,
  };
}

function tabCount(active) {
  return {
    minWidth:22, height:22, borderRadius:999, display:"grid", placeItems:"center",
    background: active?"#fff7ed":"#f1f5f9",
    color: active?"#ea580c":"#94a3b8",
    fontSize:11, fontWeight:900,
  };
}

function walletModeBtn(active, col) {
  return {
    flex:1, height:42, border:0, borderRadius:12,
    background: active?col:"#f1f5f9",
    color: active?"#fff":"#64748b",
    fontWeight:800, fontSize:14, cursor:"pointer",
    display:"flex", alignItems:"center", justifyContent:"center", gap:7,
    boxShadow: active?`0 4px 14px ${col}44`:"none",
  };
}

/* ─── static styles ─── */
const page       = { display:"grid", gap:18, fontFamily:"'Segoe UI',Inter,system-ui,sans-serif" };
const hero       = { background:"linear-gradient(135deg,#431407 0%,#7c2d12 50%,#7c2d12 100%)", borderRadius:24, padding:"24px 28px", display:"flex", alignItems:"flex-start", gap:20, flexWrap:"wrap", position:"relative" };
const backBtn    = { width:38, height:38, borderRadius:12, border:"1.5px solid rgba(255,255,255,0.2)", background:"rgba(255,255,255,0.1)", display:"grid", placeItems:"center", cursor:"pointer", flexShrink:0 };
const heroLeft   = { flex:1, display:"flex", alignItems:"flex-start", gap:18, minWidth:200 };
const heroBigAvatar= { position:"relative", width:72, height:72, borderRadius:"50%", background:"rgba(255,255,255,0.15)", border:"3px solid rgba(255,255,255,0.25)", display:"grid", placeItems:"center", flexShrink:0, overflow:"visible" };
const heroAvatarImg= { width:"100%", height:"100%", borderRadius:"50%", objectFit:"cover" };
const heroAvatarLetter={ fontSize:28, fontWeight:900, color:"#fff" };
const heroName   = { fontSize:22, fontWeight:900, color:"#fff", letterSpacing:"-0.4px" };
const heroPhone  = { fontSize:14, color:"rgba(255,255,255,0.65)", marginTop:3, fontWeight:600 };
const heroPills  = { display:"flex", gap:6, flexWrap:"wrap", marginTop:8 };
const heroJoined = { fontSize:11, color:"rgba(255,255,255,0.45)", marginTop:6, fontWeight:600 };
const heroActions= { display:"flex", gap:8, flexWrap:"wrap", alignSelf:"flex-end" };
const loadBox    = { padding:40, textAlign:"center", color:"#94a3b8", fontSize:14 };
const balStrip   = { background:"linear-gradient(135deg,#0f172a,#431407)", borderRadius:20, padding:"20px 28px", display:"flex", alignItems:"center", gap:0 };
const balCell    = { flex:1, padding:"0 8px" };
const balDiv     = { width:1, background:"rgba(255,255,255,0.12)", alignSelf:"stretch", margin:"0 8px" };
const statsGrid  = { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))", gap:12 };
const statCard   = { background:"#fff", border:"1.5px solid #e8eaf6", borderRadius:16, padding:"16px 18px", boxShadow:"0 2px 10px rgba(79,70,229,0.05)" };
const limitsCard = { background:"#fff", border:"1.5px solid #e8eaf6", borderRadius:18, padding:"20px 24px", boxShadow:"0 2px 10px rgba(79,70,229,0.05)" };
const limitsTitle= { fontSize:14, fontWeight:900, color:"#431407", marginBottom:14 };
const limitsGrid = { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:0 };
const limitRow   = { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px solid #f1f5f9" };
const dateBar    = { display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" };
const datePresets= { display:"flex", gap:8 };
const dateInputRow={ display:"flex", alignItems:"center", gap:8 };
const dateInp    = { height:34, border:"1.5px solid #e2e8f0", borderRadius:10, padding:"0 12px", fontSize:12, color:"#334155", outline:0 };
const tabBar     = { display:"flex", background:"#fff", borderRadius:0, borderBottom:"1.5px solid #e8eaf6", overflowX:"auto" };
const tableCard  = { background:"#fff", border:"1.5px solid #e8eaf6", borderRadius:18, overflow:"auto", boxShadow:"0 2px 10px rgba(79,70,229,0.05)" };
const tbl        = { width:"100%", minWidth:680, borderCollapse:"collapse" };
const th         = { height:40, padding:"0 14px", textAlign:"left", color:"#94a3b8", fontSize:10, textTransform:"uppercase", letterSpacing:"0.5px", fontWeight:700, borderBottom:"1.5px solid #e8eaf6", background:"#f8f9ff", whiteSpace:"nowrap" };
const tRow       = { borderBottom:"1px solid #f1f5f9" };
const td         = { padding:"12px 14px", color:"#334155", fontSize:13, whiteSpace:"nowrap" };
const sub        = { fontSize:10, color:"#94a3b8", marginTop:3, fontWeight:600 };
const typePill   = { display:"inline-block", padding:"3px 9px", borderRadius:6, background:"#fff7ed", color:"#ea580c", fontWeight:800, fontSize:11 };
const numWrap    = { display:"flex", gap:4, flexWrap:"wrap" };
const numChip    = { display:"inline-grid", placeItems:"center", minWidth:36, height:28, borderRadius:8, background:"#fff7ed", color:"#ea580c", fontWeight:900, fontSize:12, padding:"0 8px" };
const moreChip   = { ...numChip, background:"#f1f5f9", color:"#94a3b8" };
const viewBtn    = { width:28, height:28, border:"none", borderRadius:8, background:"#f1f5f9", color:"#ea580c", fontWeight:900, cursor:"pointer", fontSize:11 };
const expandPanel= { display:"grid", gap:6, background:"#f8f9ff", borderRadius:12, padding:12 };
const expandRow  = { display:"grid", gridTemplateColumns:"auto 1fr auto auto auto", gap:10, alignItems:"center", background:"#fff", borderRadius:10, padding:"8px 12px", border:"1px solid #e8eaf6" };
const pillBase   = { display:"inline-block", padding:"4px 10px", borderRadius:999, fontWeight:700, fontSize:11 };
const idChip     = { display:"inline-block", padding:"3px 8px", borderRadius:8, background:"#f1f5f9", color:"#64748b", fontWeight:800, fontSize:11 };
const dwIcon     = { width:22, height:22, borderRadius:6, display:"grid", placeItems:"center", flexShrink:0 };
const removeBtn  = { height:30, padding:"0 12px", border:"1px solid #fecaca", borderRadius:8, background:"#fff5f5", color:"#b91c1c", fontWeight:800, fontSize:11, cursor:"pointer" };
const restoreBtn = { height:30, padding:"0 12px", border:"1px solid #bbf7d0", borderRadius:8, background:"#f0fdf4", color:"#15803d", fontWeight:800, fontSize:11, cursor:"pointer" };
const overlay    = { position:"fixed", inset:0, background:"rgba(15,23,42,0.5)", display:"grid", placeItems:"center", zIndex:1000 };
const modalBox   = { width:480, maxWidth:"93vw", background:"#fff", borderRadius:24, overflow:"hidden", boxShadow:"0 32px 80px rgba(15,23,42,0.28)" };
const mHead      = { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"20px 24px 14px", background:"linear-gradient(135deg,#f8f9ff,#fff7ed)", borderBottom:"1.5px solid #e8eaf6" };
const mTitle     = { fontSize:18, fontWeight:900, color:"#431407" };
const mBody      = { padding:"20px 24px", display:"grid", gap:14 };
const mFoot      = { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, padding:"0 24px 24px" };
const mInp       = { height:48, border:"1.5px solid #e2e8f0", borderRadius:12, padding:"0 14px", fontSize:14, outline:0, boxSizing:"border-box", width:"100%" };
const mCancel    = { height:48, border:"1.5px solid #e2e8f0", background:"#fff", borderRadius:12, fontSize:14, fontWeight:700, cursor:"pointer", color:"#334155" };
const mSave      = { height:48, border:0, background:"linear-gradient(135deg,#ea580c,#ea580c)", color:"#fff", borderRadius:12, fontSize:14, fontWeight:800, cursor:"pointer" };
const walletModeRow={ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, background:"#f3f4f6", padding:4, borderRadius:14 };
const quickRow   = { display:"flex", gap:6, flexWrap:"wrap" };
const qBtn       = { height:34, padding:"0 12px", border:0, background:"#f1f5f9", borderRadius:10, fontWeight:700, fontSize:12, color:"#334155", cursor:"pointer" };

export default AdminUserProfile;
